import { Alert } from "@fluentui/react-northstar";
import { SurveyDTO, SurveyResponsesDTO, SurveyResultNotificationType } from "@teams/types/api";
import React, { useEffect, useState } from "react";
import { Route, Switch, useParams, useRouteMatch } from "react-router";

import { TeamsLoader } from "../../../_shared/components/TeamsLoader";
import { isError } from "../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { HotTakeInformation } from "./components/hotTake/HotTakeInformation";
import { IcebreakerInformation } from "./components/icebreaker/IcebreakerInformation";
import { QuizLeaderboard } from "./components/quiz/QuizLeaderboard";
import { UserQuizResult } from "./components/quiz/UserQuizResult";

export const SurveyResultsPage: React.FC = () => {
  const { path } = useRouteMatch();

  return (
    <div style={{ padding: "20px" }}>
      <Switch>
        <Route
          path={`${path}/quiz/leaderboard`}
          exact
          render={() => <HtmlSurveyResultsPage notificationType="quiz-leaderboard" />}
        />
        <Route
          path={`${path}/quiz/personal`}
          exact
          render={() => <HtmlSurveyResultsPage notificationType="quiz-personal" />}
        />
        <Route
          path={`${path}/qna/notification`}
          exact
          render={() => <HtmlSurveyResultsPage notificationType="qna-notification" />}
        />
        <Route
          path={`${path}/icebreaker`}
          exact
          render={() => <HtmlSurveyResultsPage notificationType="icebreaker" />}
        />
        <Route
          path={`${path}/hot-take`}
          exact
          render={() => <HtmlSurveyResultsPage notificationType="hot-take" />}
        />
      </Switch>
    </div>
  );
};

type HtmlSurveyResultsPageProps = {
  notificationType: SurveyResultNotificationType;
};

const HtmlSurveyResultsPage: React.FC<HtmlSurveyResultsPageProps> = ({ notificationType }) => {
  const params = useParams<{ surveyId: string }>();
  const { teamsContext } = useTeamsContext();

  const [survey, setSurvey] = useState<SurveyDTO>();
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponsesDTO>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [loadError, setLoadError] = useState<Error>();

  useEffect(() => {
    (async () => {
      setLoadError(undefined);
      setIsLoading(true);
      const surveyResponse = await surveyApi.survey(
        teamsContext,
        decodeURIComponent(params.surveyId)
      );

      if (!isError(surveyResponse)) {
        const { survey } = surveyResponse;
        setSurvey(survey);

        const response = await surveyApi.surveyResponses(
          teamsContext,
          decodeURIComponent(survey.id as string)
        );
        if (!isError(response)) {
          setSurveyResponses(response);
        } else {
          setLoadError(response);
        }
      } else {
        setLoadError(surveyResponse);
      }

      setIsLoading(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.surveyId]);

  if (loadError) {
    return <Alert danger content={loadError.message} />;
  }

  if (isLoading || !survey || !surveyResponses) {
    return <TeamsLoader isLoading loadingMessage="Loading results..." />;
  }

  if (notificationType === "quiz-leaderboard") {
    return <QuizLeaderboard survey={survey} responses={surveyResponses.responses} />;
  }

  if (notificationType === "quiz-personal") {
    if (!surveyResponses.userResponse) {
      return <Alert warning content="You have not completed this quiz yet" />;
    }
    return <UserQuizResult survey={survey} response={surveyResponses.userResponse} />;
  }

  if (notificationType === "icebreaker") {
    return <IcebreakerInformation surveyId={params.surveyId} />;
  }

  if (notificationType === "hot-take") {
    return <HotTakeInformation surveyId={params.surveyId} answerForm />;
  }

  return <Alert danger content="Unknown survey results type" />;
};
